// The address networks this IDE can target.
//
// Mainnet is deliberately absent, and not just from this menu: the bundled wasm
// compiler cannot encode a mainnet address at all (its network argument maps
// only onto test prefixes, and anything unrecognized falls back to testnet). A
// public playground running alpha, unaudited code must never hand out an address
// that could hold real value, so the guarantee lives in the compiler rather than
// in this UI, where a bug or a stale preference could defeat it.

export type Network = "signet" | "testnet" | "regtest";

export interface NetworkDef {
  id: Network;
  label: string;
  /** bech32m prefix of the emitted address. Signet and testnet share `tb`. */
  prefix: string;
  /** Numeric code for the wasm ABI (`bs_compile`). */
  code: number;
}

export const NETWORKS: NetworkDef[] = [
  { id: "signet", label: "Signet", prefix: "tb", code: 0 },
  { id: "testnet", label: "Testnet", prefix: "tb", code: 0 },
  { id: "regtest", label: "Regtest", prefix: "bcrt", code: 1 },
];

export const DEFAULT_NETWORK: Network = "signet";

const BY_ID = new Map(NETWORKS.map((n) => [n.id, n]));

export function networkDef(id: Network): NetworkDef {
  return BY_ID.get(id) ?? BY_ID.get(DEFAULT_NETWORK)!;
}

/** Heal a persisted or unknown value to a real network. */
export function coerceNetwork(value: unknown): Network {
  if (typeof value === "string" && BY_ID.has(value as Network)) return value as Network;
  // Older builds stored the bech32m prefix instead of the name, including "bc"
  // for mainnet. Anything unrecognized lands on the default test network.
  if (value === "bcrt") return "regtest";
  if (value === "tb") return "testnet";
  return DEFAULT_NETWORK;
}
